import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { NexsoLogo } from "@/components/app/NexsoLogo";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const bodyFont    = { fontFamily: "Manrope, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function Register() {
  const { register, error, setError } = useAuth();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const ok = await register(form);
    setLoading(false);
    if (ok) navigate("/dashboard");
  };

  return (
    <div className="min-h-screen text-slate-900 grid lg:grid-cols-2 relative overflow-hidden" style={{ background: "#FFFFFF", ...bodyFont }}>
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 right-[10%] w-[40rem] h-[40rem] rounded-full opacity-30" style={{ background: "radial-gradient(circle, #EC4899 0%, transparent 70%)", filter: "blur(80px)" }} />
        <div className="absolute bottom-[-10%] left-[-5%] w-[36rem] h-[36rem] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #8B5CF6 0%, transparent 70%)", filter: "blur(80px)" }} />
      </div>

      {/* Form left */}
      <div className="relative flex items-center justify-center p-6 md:p-12 order-2 lg:order-1">
        <form onSubmit={handleSubmit} className="w-full max-w-md" data-testid="register-form">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-8">
            <NexsoLogo size="md" />
            <span className="font-semibold text-xl" style={headingFont}>Nexso</span>
          </Link>
          <h1 className="text-4xl md:text-5xl tracking-tight font-semibold mb-3" style={headingFont}>Create your account</h1>
          <p className="text-sm text-slate-900/55 mb-8">Get 7 days of full Pro access. No credit card required.</p>

          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-[10px] uppercase tracking-[0.25em] text-slate-900/50 font-medium">Your name</Label>
                <Input id="name" value={form.name} onChange={onChange("name")} className="mt-2 rounded-xl h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-900/30 focus-visible:ring-sky-500" data-testid="register-name-input" />
              </div>
              <div>
                <Label htmlFor="biz" className="text-[10px] uppercase tracking-[0.25em] text-slate-900/50 font-medium">Business</Label>
                <Input id="biz" value={form.business_name} onChange={onChange("business_name")} className="mt-2 rounded-xl h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-900/30 focus-visible:ring-sky-500" data-testid="register-business-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.25em] text-slate-900/50 font-medium">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={onChange("email")} required className="mt-2 rounded-xl h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-900/30 focus-visible:ring-sky-500" data-testid="register-email-input" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.25em] text-slate-900/50 font-medium">Password</Label>
              <Input id="password" type="password" minLength={6} value={form.password} onChange={onChange("password")} required className="mt-2 rounded-xl h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-900/30 focus-visible:ring-sky-500" data-testid="register-password-input" autoComplete="new-password" />
              <p className="text-xs text-slate-900/40 mt-1.5">Min 6 characters.</p>
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl" data-testid="register-error">{error}</div>
            )}
            <Button type="submit" disabled={loading} className="w-full rounded-full h-12 bg-sky-500 hover:bg-sky-600 text-white shadow-md hover:shadow-lg transition-shadow" data-testid="register-submit-button">
              {loading ? "Creating…" : <>Create account <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
            <p className="text-xs text-slate-500 text-center leading-relaxed" data-testid="register-legal-notice">
              By creating an account you agree to our{" "}
              <Link to="/terms" className="text-sky-600 hover:text-sky-700 font-medium underline underline-offset-2">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-sky-600 hover:text-sky-700 font-medium underline underline-offset-2">Privacy Policy</Link>.
            </p>
          </div>

          <div className="mt-6 text-sm text-slate-900/55">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-sky-600 hover:text-sky-700" data-testid="register-to-login">Sign in</Link>
          </div>
        </form>
      </div>

      {/* Brand right */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-l order-1 lg:order-2" style={{ borderColor: "#E2E8F0" }}>
        <Link to="/" className="flex items-center gap-2.5">
          <NexsoLogo size="md" />
          <span className="font-semibold text-xl tracking-tight" style={headingFont}>Nexso</span>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-sky-300 mb-5">Get started</div>
          <h2 className="text-5xl xl:text-6xl leading-[1.05] tracking-tight font-semibold" style={headingFont}>
            One account.<br/>Every{" "}
            <span style={accentFont} className="text-sky-300">screen</span>.
          </h2>
          <p className="mt-6 text-slate-900/60 max-w-md text-base leading-relaxed">
            Pair as many TVs as you need. Push menu changes in seconds. Designed for restaurants, cafes, retail and services.
          </p>
        </div>
        <div className="text-xs text-slate-900/30">© {new Date().getFullYear()} Nexso</div>
      </div>
    </div>
  );
}
